/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import {
  AbstractFormComponent,
  BuilderNavigationService,
  BuilderUserContext,
} from '@gh/builder-shared';
import {
  getWorksheetId,
  JobBillingWorksheet,
  JobBillingWorksheetItem,
} from '@gh/builder-shared-data';
import {
  AppRouter,
  DateTimeTypes,
  FormStateBuilder,
  getRouteInitData,
  NumberTypes,
} from '@gh/common-shared';
import { DateFormat, RouteDataService } from '@gh/core-data';
import {
  DialogService,
  noAnswer,
  NotificationService,
  TextMessageDialogConfig,
  WaitingContext,
  WaitingMode,
  WaitingService,
  yesAnswer,
  yesNoCancelConfirmation,
} from '@gh/core-ui';
import {
  buildEditableDateTimeColumn,
  buildEditableNumberColumn,
  buildNumberColumnLeft,
  GridDefinitionFactory,
  LayoutBasedGridDefinition,
} from '@gh/core-ui-grid';
import { CmListBusinessService } from '../../resources/cm-list-business.service';
import { TranslationService } from '@gh/core-mls';
import {
  GridSettingsService,
  textMessageDialogRoute,
  createDefaultGridModel,
} from '@gh/common-internal-shared';
import { createLink } from '@gh/core-util';
import { CmWorksheetFormState } from './cm-worksheet-form-state.service';
import { Observable } from 'rxjs';
import { JobBillingWorksheetBusinessService } from '../../resources/cm-worksheet-business.service';
import { filter$, map$, switchMap$ } from '@gh/rx/operators';

@Component({
  standalone: false,
  selector:    'gh-cm-worksheet',
  styleUrls:   ['cm-worksheet.component.scss'],
  templateUrl: 'cm-worksheet.component.html',
  providers:   [CmWorksheetFormState],
})
export class CmWorksheetComponent extends AbstractFormComponent<JobBillingWorksheet> implements OnInit, OnDestroy {
  gridDefinition: LayoutBasedGridDefinition;
  filter: any;
  dirtyItems: JobBillingWorksheetItem[];
  parentUrl: string;
  isLoading: boolean;

  get deactivateFlow(): any {
    return this.onProcess();
  }

  get hasChanges(): boolean {
    return !!this.dirtyItems.length;
  }

  constructor(private route: ActivatedRoute,
    private routeData: RouteDataService,
    private userContext: BuilderUserContext,
    private formStateBuilder: FormStateBuilder,
    public form: CmWorksheetFormState,
    private businessService: CmListBusinessService,
    private worksheetService: JobBillingWorksheetBusinessService,
    private notifications: NotificationService,
    private router: AppRouter,
    private angRouter: Router,
    private translations: TranslationService,
    private dialogs: DialogService,
    private waitingService: WaitingService,
    private navigationService: BuilderNavigationService,
    private gridService: GridSettingsService,
    private waitingContext: WaitingContext,
    private notificationService: NotificationService,
    public gridDefinitionFactory: GridDefinitionFactory,
  ) {
    super();
  }

  get isProcessEnabled(): boolean {
    return !!this.dirtyItems.length && !this.form.invalid;
  }

  ngOnInit(): void {
    const { gridLayout, details, filter } = getRouteInitData(this.route);
    this.filter = filter;
    this.parentUrl = `/contracts-manager?filter=${JSON.stringify(this.filter)}`;
    this.form.build(details);

    const model = createDefaultGridModel({
      editable:   true,
      getId:      getWorksheetId,
      getCellDef: this.form.billingWorksheetFieldState,
      command:    (entity, field, value) => this.form.setWorksheetField(entity, field, value),
    });
    this.gridDefinition = this.gridDefinitionFactory.buildLayoutBasedWithModel(
      {
        model,
        layout:  gridLayout,
        columns: {
          contractAmount:          buildNumberColumnLeft(NumberTypes.Price),
          totalOfficeFee:          buildNumberColumnLeft(NumberTypes.Price),
          totalOfficeVariationFee: buildNumberColumnLeft(NumberTypes.Price),
          officePaymentDue:        buildNumberColumnLeft(NumberTypes.Price),
          officePaidToDate:        buildNumberColumnLeft(NumberTypes.Price),

          officePaidAmount:        buildEditableNumberColumn(NumberTypes.SignedPrice, model.getCellAccessor('officePaidAmount')),
          totalMasterFee:          buildNumberColumnLeft(NumberTypes.Price),
          totalMasterVariationFee: buildNumberColumnLeft(NumberTypes.Price),
          masterPaymentDue:        buildNumberColumnLeft(NumberTypes.Price),
          masterPaidToDate:        buildNumberColumnLeft(NumberTypes.Price),
          masterLastPaymentDate:   {
            ...buildEditableDateTimeColumn(DateTimeTypes.ShortDate, model.getCellAccessor('masterLastPaymentDate'), {
              format:         DateFormat.Date,
              forceClearable: false,
              isTimeAllowed:  () => false,
            }),
          },
          officeLastPaymentDate:   {
            ...buildEditableDateTimeColumn(DateTimeTypes.ShortDate, model.getCellAccessor('officeLastPaymentDate'), {
              format:         DateFormat.Date,
              forceClearable: false,
              clearable: false,
              isTimeAllowed:  () => false,
            }),
          },
          masterPaidAmount:        buildEditableNumberColumn(NumberTypes.SignedPrice, model.getCellAccessor('masterPaidAmount')),
        },
        options: {
          getRowId: (params) => String(getWorksheetId(params.data)),
        },
      });
    this.gridDefinition.bind(gridLayout);
    this.form.items$.subscribe((items) => {
      const isOfficeShow = this.gridDefinition.layout.columns.find((column) => column.column === 'officePaidAmount' && !!column.visible);
      this.dirtyItems = isOfficeShow ? items.filter(
        (item) =>  {
          const originItem =  this.form.originalValue.items.find((origin) => origin.id === item.id);
          return !!item.officePaidAmount || originItem?.officeLastPaymentDate !== item.officeLastPaymentDate;
        })
        : items.filter((item) => {
          const originItem =  this.form.originalValue.items.find((origin) => origin.id === item.id);
          return !!item.masterPaidAmount || originItem?.masterLastPaymentDate !== item.masterLastPaymentDate;
        });
    });
  }

  onProcess(): void {
    const process = this.waitingContext.start(WaitingMode.Blocked);
    this.onComplete().subscribe(
      () => {
        process.stop();
      }, (err) => {
        process.stop();
        this.isLoading = false;
        this.notifications.processAll(err);
        },
    );
  }

  onCancel(): void {
    if (this.dirtyItems.length) {
      const confirmation$ = this.dialogs.confirmation(yesNoCancelConfirmation('msg_unsaved_info'));
      confirmation$.pipe(filter$(yesAnswer)).subscribe(() => this.onComplete()
        .subscribe(() => this.router.navigateByUrl(this.parentUrl)));
      confirmation$.pipe(filter$(noAnswer)).subscribe(() => {
        this.form.setDirty(false);
        this.dirtyItems = [];
        this.router.navigateByUrl(this.parentUrl);
      });
      return;
    }
    this.router.navigateByUrl(this.parentUrl);
  }

  ngOnDestroy(): void {
    this.gridService.leaveTable();
  }

  onExport(): void {
    const columns = this.gridDefinition.layout.columns.map((column) =>  ({
      width: column.width,
      column: column.column,
      visible: column.visible,
      position: column.position,
      sortable: column.sortable,
      resizable: column.resizable,
    })).filter((column) => column.visible && column.column !== 'officePaidAmount' && column.column !== 'masterPaidAmount');
    const process = this.waitingContext.start(WaitingMode.Background);
    this.worksheetService.export(this.form.originalValue.items, columns).subscribe(
      ({ url }) => {
        this.dialogs.openWithInput(textMessageDialogRoute, <TextMessageDialogConfig<string>>{
          title:   this.translations.get('lbl_link_to_google_sheet'),
          html:    `${ this.translations.get('lbl_link_to_generated_doc') }:\n${ createLink(url) }`,
          buttons: [{ value: 'ok', title: this.translations.get('lbl_ok'), type: 'primary' }],
        }).afterClosed();
        process.stop();
      }, (error) => {
        this.notificationService.processAll(error);
        process.stop();
      });
  }

  canDeactivate(): Observable<boolean> | boolean {
    if (!this.hasChanges) {
      return true;
    }
    const confirmation$ = this.dialogs.confirmation(yesNoCancelConfirmation('msg_unsaved_info'));

    confirmation$.pipe(filter$(yesAnswer),
                       switchMap$(() => this.onComplete().pipe(
                         this.waitingService.operator()))).subscribe(() => {}, this.notifications.processAll);
    confirmation$.pipe(filter$(noAnswer)).subscribe(() => true);
    return confirmation$.pipe(map$((res) => !!res));
  }

  onComplete(): Observable<any> {
    const addedList = this.dirtyItems.map((item) => ({
      id: item.id,
      jobNumber: item.jobNumber,
      officeId: item.officeId,
      contractType: item.contractType,
      contractTypeValue: item.contractTypeValue,
      contractAmount: item.contractAmount,
      totalOfficeFee: item.totalOfficeFee,
      totalOfficeVariationFee: item.totalOfficeVariationFee,
      officePaymentDue: item.officePaymentDue,
      officePaidToDate: item.officePaidToDate,
      officeLastPaymentDate: item.officeLastPaymentDate,
      officePaymentDate: item.officePaymentDate,
      officePaidAmount: item.officePaidAmount,
      totalMasterFee: item.totalMasterFee,
      totalMasterVariationFee: item.totalMasterVariationFee,
      masterPaymentDue: item.masterPaymentDue,
      masterPaidToDate: item.masterPaidToDate,
      masterLastPaymentDate: item.masterLastPaymentDate,
      masterPaymentDate: item.masterPaymentDate,
      masterPaidAmount: item.masterPaidAmount,
      comments: item.comments,
    }));
    this.isLoading = true;
    return this.worksheetService.save(addedList).pipe(map$((res) => {
      this.form.originalValue.items = this.form.originalValue.items.map((originItem) => {
       const target = res.items.find((item) => item.id === originItem.id);
       if (target) {
         return { ...target};
       }
       return originItem;
      });
      this.form.setValue(this.form.originalValue);
      this.form.setDirty(false);
      this.dirtyItems = [];
      this.notificationService.success('msg_saved_successfully');
      this.isLoading = false;
      return !!res;
    }));
  }
}
